/**
 * Espace apprenant personnalisé (04/11/2026, demande du dirigeant) :
 *  - `EnTeteApprenant` : l'en-tête façon Notion (couverture, photo et nom du formateur, intitulé, message d'accueil) ;
 *  - `PlanApprenant` : l'arborescence Formation › Avant / Pendant / Après › documents et formulaires ;
 *  - `ReglagesAccueilProfil` (profil du formateur) et `AccueilDuDossier` (surcharge par dossier).
 * Les données viennent de `/api/accueil-apprenant/…` et du dossier déjà chargé : aucun nouveau statut.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListTree } from "lucide-react";
import { arbreApprenant, type NoeudArbre } from "@/domaine/accueil-apprenant/arbre";
import type { lireAccueilDossier, lireAccueilProfil } from "@/serveur/services/accueil-apprenant";
import { api, ErreurApi, type CoffreApprenant, type Dossier, type Json } from "../api";
import { Arborescence } from "../ui/Arborescence";
import { Alerte, Carte, Chargement, useNotifier } from "../ui/base";
import { BanniereAccueil, EditeurAccueil } from "../ui/Couverture";

type AccueilDossier = Json<Awaited<ReturnType<typeof lireAccueilDossier>>>;
type AccueilProfil = Json<Awaited<ReturnType<typeof lireAccueilProfil>>>;

export function EnTeteApprenant({ d, children }: { d: Dossier; children?: React.ReactNode }) {
  const accueil = useQuery({
    queryKey: ["accueil-dossier", d.id],
    queryFn: () => api.get<AccueilDossier>(`/accueil-apprenant/dossiers/${d.id}`),
  });
  if (!accueil.data) {
    // En attendant (ou en cas d'échec), l'essentiel reste lisible : l'intitulé et la frise.
    return (
      <header className="rounded-lg border border-trait bg-carte p-5 shadow-carte">
        <h1 className="font-display text-[22px] leading-tight font-semibold sm:text-[28px]">
          {d.formation.formation_titre}
        </h1>
        {children}
      </header>
    );
  }
  return (
    <BanniereAccueil
      accueil={accueil.data.accueil}
      formateur={accueil.data.formateur}
      titre={d.formation.formation_titre}
    >
      {children}
    </BanniereAccueil>
  );
}

/** Défile jusqu'à l'ancre et la signale un instant (sans animation si l'utilisateur la refuse). */
function allerA(cible: string) {
  const el = document.getElementById(cible.replace(/^#/, ""));
  if (!el) return;
  const reduit = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduit ? "auto" : "smooth", block: "start" });
  el.setAttribute("data-signale", "");
  window.setTimeout(() => el.removeAttribute("data-signale"), 1600);
  if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
  el.focus({ preventScroll: true });
}

export function PlanApprenant({
  d,
  stagiaireId,
  sections,
  coffre,
}: {
  d: Dossier;
  stagiaireId: string;
  sections: readonly string[];
  coffre: CoffreApprenant | undefined;
}) {
  const racine = useMemo(
    () =>
      arbreApprenant({
        dossier_id: d.id,
        formation_titre: d.formation.formation_titre,
        sections,
        supports: coffre ? coffre.fichiers.length : null,
        questionnaires: d.questionnaires_etat.filter((q) => q.stagiaire_id === stagiaireId),
        pieces: d.pieces,
      }),
    [d, stagiaireId, sections, coffre],
  );
  const [selection, setSelection] = useState<string | null>(null);
  const activer = (n: NoeudArbre) => {
    setSelection(n.id);
    if (n.cible?.startsWith("#")) allerA(n.cible);
  };
  // Ouverts d'emblée : la formation, et les étapes où il reste quelque chose à faire.
  const ouverts = [racine.id, ...racine.enfants.filter((e) => e.etat === "a_faire").map((e) => e.id)];
  const arbre = (
    <Arborescence
      racines={[racine]}
      libelle="Plan de mon espace"
      ouvertsInitiaux={ouverts}
      selection={selection}
      activer={activer}
    />
  );
  return (
    <>
      {/* Mobile et tablette : un plan repliable au-dessus du contenu. */}
      <details
        className="group mb-5 rounded-lg border border-trait bg-carte shadow-carte lg:hidden"
        data-testid="plan-mobile"
      >
        <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 px-4 text-sm font-semibold [&::-webkit-details-marker]:hidden">
          <ListTree className="size-4 text-accent" aria-hidden />
          Plan de mon espace
          <span className="ml-auto text-[12px] font-medium text-encre-3 group-open:hidden">Afficher</span>
          <span className="ml-auto hidden text-[12px] font-medium text-encre-3 group-open:inline">
            Masquer
          </span>
        </summary>
        <div className="border-t border-trait p-2">{arbre}</div>
      </details>
      {/* Ordinateur : colonne latérale collante. */}
      <aside
        className="sticky top-4 hidden max-h-[calc(100vh-2rem)] self-start overflow-y-auto rounded-lg border border-trait bg-carte p-2 shadow-carte lg:block"
        aria-label="Plan de mon espace"
        data-testid="plan-bureau"
      >
        <p className="flex items-center gap-2 px-2 pt-1.5 pb-2 text-[12px] font-semibold tracking-wide text-encre-3 uppercase">
          <ListTree className="size-4 text-accent" aria-hidden /> Plan de mon espace
        </p>
        {arbre}
      </aside>
    </>
  );
}

function erreursChamps(e: unknown): Record<string, string> | undefined {
  return e instanceof ErreurApi && e.details?.champs ? e.details.champs : undefined;
}

/** Profil du formateur : l'accueil PAR DÉFAUT de tous ses apprenants. */
export function ReglagesAccueilProfil() {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const q = useQuery({
    queryKey: ["accueil-apprenant"],
    queryFn: () => api.get<AccueilProfil>("/accueil-apprenant"),
  });
  const maj = (r: AccueilProfil) => {
    requetes.setQueryData(["accueil-apprenant"], r);
    void requetes.invalidateQueries({ queryKey: ["accueil-dossier"] });
  };
  const echec = (e: Error) =>
    notifier("danger", e instanceof ErreurApi ? e.message : "Enregistrement impossible. Réessayez.");
  const enregistrer = useMutation({
    mutationFn: (s: object) => api.put<AccueilProfil>("/accueil-apprenant", s),
    onSuccess: (r) => (
      maj(r),
      notifier("succes", "Accueil enregistré : vos apprenants le verront à leur prochaine visite.")
    ),
    onError: echec,
  });
  const deposer = useMutation({
    mutationFn: (f: File) => api.fichier<AccueilProfil>("/accueil-apprenant/image", f),
    onSuccess: (r) => (maj(r), notifier("succes", "Image de couverture enregistrée.")),
    onError: echec,
  });
  const retirer = useMutation({
    mutationFn: () => api.suppr<AccueilProfil>("/accueil-apprenant/image"),
    onSuccess: (r) => (maj(r), notifier("succes", "Image retirée.")),
    onError: echec,
  });
  if (q.isPending) return <Chargement />;
  if (q.error) return <Alerte ton="danger">{q.error.message}</Alerte>;
  return (
    <Carte className="p-5" data-testid="reglages-accueil">
      <h2 className="text-lg font-semibold">Accueil de vos apprenants</h2>
      <p className="mt-1 mb-5 text-sm text-encre-2">
        La couverture et le message qui accueillent chaque apprenant en haut de son espace, avec votre photo
        de profil. Gardez votre ton : vous pourrez les adapter dossier par dossier.
      </p>
      <EditeurAccueil
        niveau={q.data.reglages}
        heritage={undefined}
        formateur={q.data.formateur}
        titreApercu="Intitulé de votre formation"
        enregistrer={(s) => enregistrer.mutate(s)}
        deposer={(f) => deposer.mutate(f)}
        retirerImage={() => retirer.mutate()}
        enCours={{ enregistrer: enregistrer.isPending, deposer: deposer.isPending }}
        erreurs={erreursChamps(enregistrer.error)}
      />
    </Carte>
  );
}

/** Fiche dossier (formateur, organisme) : surcharger l'accueil pour CE dossier. Replié par défaut. */
export function AccueilDuDossier({ d }: { d: Dossier }) {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const q = useQuery({
    queryKey: ["accueil-dossier", d.id],
    queryFn: () => api.get<AccueilDossier>(`/accueil-apprenant/dossiers/${d.id}`),
  });
  const maj = (r: AccueilDossier) => requetes.setQueryData(["accueil-dossier", d.id], r);
  const echec = (e: Error) =>
    notifier("danger", e instanceof ErreurApi ? e.message : "Enregistrement impossible. Réessayez.");
  const base = `/accueil-apprenant/dossiers/${d.id}`;
  const enregistrer = useMutation({
    mutationFn: (s: object) => api.put<AccueilDossier>(base, s),
    onSuccess: (r) => (maj(r), notifier("succes", "Accueil de ce dossier enregistré.")),
    onError: echec,
  });
  const deposer = useMutation({
    mutationFn: (f: File) => api.fichier<AccueilDossier>(`${base}/image`, f),
    onSuccess: (r) => (maj(r), notifier("succes", "Image de couverture enregistrée.")),
    onError: echec,
  });
  const retirer = useMutation({
    mutationFn: () => api.suppr<AccueilDossier>(`${base}/image`),
    onSuccess: maj,
    onError: echec,
  });
  const reinitialiser = useMutation({
    mutationFn: () => api.suppr<AccueilDossier>(base),
    onSuccess: (r) => (maj(r), notifier("succes", "Ce dossier reprend l'accueil de votre profil.")),
    onError: echec,
  });
  if (!q.data?.reglages) return null;
  const perso = q.data.accueil.surcharge.couverture || q.data.accueil.surcharge.message;
  return (
    <details
      className="group rounded-lg border border-trait bg-carte shadow-carte"
      data-testid="accueil-dossier"
    >
      <summary className="flex min-h-12 cursor-pointer list-none flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 [&::-webkit-details-marker]:hidden">
        <span className="font-semibold">Accueil de l'espace apprenant</span>
        <span className="text-[13px] text-encre-3">
          {perso ? "Personnalisé pour ce dossier" : "Celui de votre profil"}
        </span>
        <span className="ml-auto text-[13px] font-medium text-accent group-open:hidden">Personnaliser</span>
      </summary>
      <div className="border-t border-trait p-4 sm:p-5">
        <EditeurAccueil
          niveau={q.data.reglages.surcharge}
          heritage={q.data.reglages.defaut}
          formateur={q.data.formateur}
          titreApercu={d.formation.formation_titre}
          enregistrer={(s) => enregistrer.mutate(s)}
          deposer={(f) => deposer.mutate(f)}
          retirerImage={() => retirer.mutate()}
          reinitialiser={perso ? () => reinitialiser.mutate() : undefined}
          enCours={{ enregistrer: enregistrer.isPending, deposer: deposer.isPending }}
          erreurs={erreursChamps(enregistrer.error)}
        />
      </div>
    </details>
  );
}
